import { User } from '@kalos/kalos-rpc';
import { Button, Dialog, DialogContent, DialogTitle, Textarea, toast } from '@kalos/ui';
import { format } from 'date-fns';
import { useState } from 'react';

import { useAuth } from '../../../../../context/AuthContext';
import { useUserUpdateMutation } from '../../../../../hooks/react-query/useUserQuery';
import { InternalNoteFormattedText } from './InternalNoteFormattedText';

interface EndWithoutSchedulingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: User;
  onEnded: () => void;
}

const TEXT =
  "This will close the wizard without creating a job. You can optionally leave a note that will be appended to the customer's internal note.";

export const EndWithoutSchedulingDialog = ({
  open,
  onOpenChange,
  customer,
  onEnded,
}: EndWithoutSchedulingDialogProps) => {
  const { user: authUser } = useAuth();
  const userMutation = useUserUpdateMutation();

  const existingNote = customer.intNotes || '';
  const [newNote, setNewNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => {
    setNewNote('');
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    const trimmed = newNote.trim();
    if (!trimmed) {
      handleClose();
      onEnded();
      return;
    }

    const stamp = format(new Date(), 'MM-dd-yyyy HH:mm');
    const author = `${authUser.firstname || ''} ${authUser.lastname || ''}`.trim() || 'Unknown';
    const line = `[${stamp}] ${author}: ${trimmed}`;
    const updated = existingNote ? `${existingNote}\n${line}` : line;

    setIsSaving(true);
    try {
      await userMutation.mutateAsync(
        User.create({ id: customer.id, intNotes: updated, fieldMask: ['IntNotes'] }),
      );
      toast({ variant: 'success', title: 'Internal note saved' });
      setNewNote('');
      onOpenChange(false);
      onEnded();
    } catch (error) {
      console.error(error);
      toast({ variant: 'destructive', title: 'Failed to save internal note' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : handleClose())}>
      <DialogContent className="bg-white">
        <DialogTitle>End without scheduling</DialogTitle>
        <p className="text-sm text-gray-500">{TEXT}</p>

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Existing Internal Note
          </p>
          {existingNote ? (
            <div className="max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-2 text-sm">
              <InternalNoteFormattedText text={existingNote} />
            </div>
          ) : (
            <p className="text-sm text-gray-400">No internal note set</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="new-internal-note" className="text-sm font-medium text-gray-700">
            Add note (optional)
          </label>
          <Textarea
            id="new-internal-note"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            rows={4}
            placeholder="Add a note..."
            className="bg-white"
            disabled={isSaving}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'End Without Scheduling'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
